import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import SupplierFilterPanel, { RfqStrip } from "@/components/SupplierFilterPanel";
import ListingGrid from "@/components/ListingGrid";
import { toast } from "sonner";
import CategoryRFQModal from "@/components/CategoryRFQModal";
import { MapPin, FileText, ShieldCheck } from "lucide-react";

const inputCls = "w-full px-3 py-2 text-sm border border-[#C4CFC9] bg-white rounded-sm focus:border-[#264839] focus:outline-none";
const labelCls = "block text-xs font-medium text-[#4A5550] mb-1.5";
const CITY_SUGGESTIONS = ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide", "Auckland", "Wellington", "London", "Manchester", "New York", "Los Angeles", "Chicago", "Toronto", "Vancouver", "Dubai", "Singapore", "Hong Kong", "Tokyo", "Bangkok", "Bali", "Paris", "Berlin", "Madrid", "Rome"];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [tree, setTree] = useState([]);
  const PAGE_SIZE = 30;
  const [results, setResults] = useState({ items: [], total: 0, free_total: 0 });
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rfqOpen, setRfqOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [q, setQ] = useState(params.get("q") || "");
  const [qInput, setQInput] = useState(params.get("q") || "");
  useEffect(() => { setQInput(q); }, [q]);
  const [customOem, setCustomOem] = useState(params.get("custom_oem") === "true");
  const [parentCategoryId, setParentCategoryId] = useState(params.get("parent_category_id") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [stateCode, setStateCode] = useState(params.get("state_code") || "");
  const [country, setCountry] = useState(params.get("country") || "");
  const [supplierTypes, setSupplierTypes] = useState(() => (params.get("supplier_type") || "").split(",").filter(Boolean));
  const [filterOptions, setFilterOptions] = useState({ countries: [], supplier_types: [] });
  useEffect(() => { api.get("/listings/filter-options").then(({ data }) => setFilterOptions(data)).catch(() => {}); }, []);
  const [shipsTo, setShipsTo] = useState(params.get("ships_to") || "");
  const [tier, setTier] = useState(params.get("tier") || "");
  const [featured, setFeatured] = useState(params.get("featured") === "true");
  const [verifiedOnly, setVerifiedOnly] = useState(params.get("verified") === "true");
  const [activeTags, setActiveTagsRaw] = useState(() => {
    const raw = params.get("tags") || "";
    return raw ? raw.split(",").filter(Boolean) : [];
  });
  const setActiveTags = (next) => setActiveTagsRaw(Array.from(new Set(next)).filter(Boolean));
  const toggleTag = (t) => setActiveTags(activeTags.includes(t) ? activeTags.filter((x) => x !== t) : [...activeTags, t]);
  const [tagSearch, setTagSearch] = useState("");

  useEffect(() => { api.get("/categories/tree").then(({ data }) => setTree(data)); }, []);

  const selectedParent = useMemo(() => tree.find((p) => p.id === parentCategoryId) || null, [tree, parentCategoryId]);

  const buildParams = (skip) => {
    const qp = new URLSearchParams();
    if (q) qp.set("q", q);
    if (parentCategoryId) qp.set("parent_category_id", parentCategoryId);
    if (activeTags.length > 0) qp.set("tags", activeTags.join(","));
    if (city) qp.set("city", city);
    if (stateCode) qp.set("state_code", stateCode.trim().toUpperCase());
    if (country) qp.set("country", country);
    if (supplierTypes.length) qp.set("supplier_type", supplierTypes.join(","));
    if (shipsTo) qp.set("ships_to", shipsTo.trim());
    if (tier) qp.set("tier", tier);
    if (featured) qp.set("featured", "true");
    if (verifiedOnly) qp.set("verified", "true");
    if (customOem) qp.set("custom_oem", "true");
    qp.set("limit", String(PAGE_SIZE));
    if (skip) qp.set("skip", String(skip));
    return qp;
  };

  const fetchResults = async () => {
    setLoading(true);
    const qp = buildParams(0);
    try {
      const { data } = await api.get(`/listings?${qp.toString()}`);
      setResults(data);
      qp.delete("limit");
      setParams(qp, { replace: true });
    } finally { setLoading(false); }
  };

  // Skip-based "Load more" (same approach as CategoryPage) — appends the next page, de-duplicated by id.
  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get(`/listings?${buildParams(results.items.length).toString()}`);
      setResults((prev) => {
        const seen = new Set(prev.items.map((i) => i.id));
        return { ...data, items: [...prev.items, ...(data.items || []).filter((i) => !seen.has(i.id))] };
      });
    } catch { toast.error("Couldn't load more suppliers"); }
    finally { setLoadingMore(false); }
  };

  // Every filter applies live; location inputs are debounced, everything else fires immediately.
  // The keyword box is committed with the Search button / Enter key (`q`), not while typing (`qInput`).
  const prevText = React.useRef(null);
  useEffect(() => {
    const text = JSON.stringify([city, stateCode, shipsTo]);
    const textChanged = prevText.current !== null && prevText.current !== text;
    prevText.current = text;
    const t = setTimeout(fetchResults, textChanged ? 350 : 0);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q, city, stateCode, shipsTo, country, supplierTypes, parentCategoryId, activeTags, tier, featured, verifiedOnly, customOem]);

  const advancedCount = (parentCategoryId ? 1 : 0) + activeTags.length + (city ? 1 : 0) + (stateCode ? 1 : 0) + (shipsTo ? 1 : 0) + (tier ? 1 : 0) + (featured ? 1 : 0) + (verifiedOnly ? 1 : 0);
  const anyActive = !!(q || country || supplierTypes.length || customOem || advancedCount);
  const clearAll = () => {
    setQ(""); setQInput(""); setCountry(""); setSupplierTypes([]); setParentCategoryId(""); setActiveTags([]); setTagSearch(""); setCustomOem(false);
    setCity(""); setStateCode(""); setShipsTo(""); setTier(""); setFeatured(false); setVerifiedOnly(false);
  };
  const clearSearch = () => { setQ(""); setQInput(""); };
  const rfqCategory = selectedParent || tree.find((p) => !p.is_coming_soon) || null;

  const allTags = selectedParent?.tag_filters || [];
  const TIER_LABELS = { leader: "Category Leader", growth: "Catalogue Growth", starter: "Starter", free: "Free" };
  const activeChips = [
    ...(q ? [{ key: "q", group: "Search", label: q, onRemove: clearSearch }] : []),
    ...(parentCategoryId ? [{ key: "category", group: "Category", label: selectedParent?.name || "Category", onRemove: () => { setParentCategoryId(""); setActiveTags([]); } }] : []),
    ...(country ? [{ key: "country", group: "Country", label: country, onRemove: () => setCountry("") }] : []),
    ...(city ? [{ key: "city", group: "City", label: city, onRemove: () => setCity("") }] : []),
    ...(stateCode ? [{ key: "state", group: "State", label: stateCode, onRemove: () => setStateCode("") }] : []),
    ...(shipsTo ? [{ key: "ships-to", group: "Ships to", label: shipsTo, onRemove: () => setShipsTo("") }] : []),
    ...supplierTypes.map((t) => ({ key: `type-${t.toLowerCase()}`, group: "Type", label: t, onRemove: () => setSupplierTypes(supplierTypes.filter((x) => x !== t)) })),
    ...(tier ? [{ key: "tier", group: "Tier", label: TIER_LABELS[tier] || tier, onRemove: () => setTier("") }] : []),
    ...(featured ? [{ key: "featured", label: "Featured only", onRemove: () => setFeatured(false) }] : []),
    ...(verifiedOnly ? [{ key: "verified", label: "Verified only", onRemove: () => setVerifiedOnly(false) }] : []),
    ...(customOem ? [{ key: "oem", label: "Custom / OEM", onRemove: () => setCustomOem(false) }] : []),
    ...activeTags.map((t) => ({ key: `tag-${t}`, group: "Tag", label: allTags.find((tf) => tf.tag === t)?.label || t.replace(/-/g, " "), onRemove: () => toggleTag(t) })),
  ];
  const tagNeedle = tagSearch.trim().toLowerCase().replace(/-/g, " ");
  const shownTags = tagNeedle
    ? allTags.filter((tf) => (tf.label || "").toLowerCase().includes(tagNeedle) || (tf.tag || "").replace(/-/g, " ").includes(tagNeedle))
    : allTags.filter((tf) => tf.visible !== false);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="search-page">
      <div className="mb-4">
        <div className="tracking-overline mb-1">Advanced search</div>
        <h1 className="font-serif text-3xl md:text-4xl text-[#11211A] tracking-tight">Find the right supplier</h1>
      </div>

      {selectedParent && (
        <RfqStrip categoryName={selectedParent.name} onClick={() => setRfqOpen(true)} disabled={results.items.length === 0} testPrefix="search" />
      )}

      <SupplierFilterPanel
        q={qInput} onQ={setQInput} onSubmit={(v) => setQ((v ?? qInput).trim())}
        searchPlaceholder="Keyword, product or supplier name — e.g. towels or Bambusa"
        country={country} onCountry={setCountry}
        countryOptions={[
          ...filterOptions.countries.map((c) => ({ value: c.name, label: c.name, count: c.count })),
          ...(country && !filterOptions.countries.some((c) => c.name === country) ? [{ value: country, label: country }] : []),
        ]}
        supplierTypes={supplierTypes} onSupplierTypes={setSupplierTypes}
        customOem={customOem} onCustomOem={setCustomOem}
        anyActive={anyActive}
        onClear={clearAll}
        advancedCount={advancedCount}
        activeChips={activeChips}
        advancedOpen={advancedOpen}
        onToggleAdvanced={() => setAdvancedOpen((o) => !o)}
        testPrefix="filter"
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="search-advanced-filters">
          <div>
            <label className={labelCls}>Category</label>
            <select value={parentCategoryId} onChange={(e) => { setParentCategoryId(e.target.value); setActiveTags([]); }} className={inputCls} data-testid="filter-category">
              <option value="">All categories</option>
              {tree.filter((p) => !p.is_coming_soon).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>City</label>
            <input list="city-suggestions" value={city} onChange={(e) => setCity(e.target.value)} className={inputCls} data-testid="filter-city" placeholder="Type to search…" autoComplete="off" />
            <datalist id="city-suggestions" data-testid="city-suggestions">{CITY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}</datalist>
          </div>
          <div>
            <label className={labelCls}>State / Province</label>
            <input value={stateCode} onChange={(e) => setStateCode(e.target.value.toUpperCase())} placeholder="e.g. NSW, ON" maxLength={5} className={`${inputCls} uppercase`} data-testid="filter-state-code" />
          </div>
          <div>
            <label className={`${labelCls} flex items-center gap-1.5`}><MapPin className="w-3.5 h-3.5 text-[#264839]" /> Ships to my location</label>
            <input value={shipsTo} onChange={(e) => setShipsTo(e.target.value)} placeholder="e.g. Sydney, NSW, Australia" className={inputCls} data-testid="filter-ships-to" />
          </div>
          <div>
            <label className={labelCls}>Tier</label>
            <select value={tier} onChange={(e) => setTier(e.target.value)} className={inputCls} data-testid="filter-tier">
              <option value="">Any</option>
              <option value="leader">Category Leader</option>
              <option value="growth">Catalogue Growth</option>
              <option value="starter">Starter</option>
              <option value="free">Free</option>
            </select>
          </div>
          <div className="flex flex-col justify-end gap-2">
            <label className="flex items-center gap-2 text-sm text-[#4A5550] cursor-pointer">
              <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} className="accent-[#264839]" data-testid="filter-featured" /> Featured only
            </label>
            <label className="flex items-center gap-2 text-sm text-[#4A5550] cursor-pointer">
              <input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} className="accent-[#264839]" data-testid="filter-verified" />
              <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-700" /> Verified Suppliers only</span>
            </label>
          </div>
          {allTags.length > 0 && (
            <div className="md:col-span-2 lg:col-span-4 pt-3 border-t border-[#E8ECE9]" data-testid="filter-tags-block">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <label className="text-xs uppercase tracking-widest text-[#B35C4A]">Filter by product type</label>
                <input type="text" value={tagSearch} onChange={(e) => setTagSearch(e.target.value)} placeholder="Search tags (e.g. face cloth, quilt)…" className="px-2.5 py-1.5 text-[11px] border border-[#C4CFC9] rounded-sm min-w-[220px]" data-testid="filter-tag-search" autoComplete="off" />
                {activeTags.length > 0 && <button type="button" onClick={() => setActiveTags([])} className="text-[10px] text-[#B35C4A] hover:underline" data-testid="filter-tags-clear">Clear tags</button>}
              </div>
              {shownTags.length === 0 ? (
                <div className="text-[10px] text-[#7D8C85] italic">No tags match "{tagSearch}".</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {shownTags.map((tf) => {
                    const on = activeTags.includes(tf.tag);
                    return (
                      <button key={tf.tag} type="button" onClick={() => toggleTag(tf.tag)} className={`text-[11px] px-2.5 py-1 border transition ${on ? "bg-[#264839] text-white border-[#264839]" : "bg-white text-[#4A5550] border-[#C4CFC9] hover:border-[#264839]"}`} data-testid={`filter-tag-${tf.tag}`}>
                        {tf.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </SupplierFilterPanel>

      <div data-testid="search-results">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="text-sm text-[#7D8C85]" data-testid="search-result-count">
            {loading ? "Searching…" : q
              ? <span className="text-[#11211A] font-medium" data-testid="search-result-summary">{results.total} supplier{results.total === 1 ? "" : "s"} for “{q}”</span>
              : `${results.total} results${anyActive ? " · filtered" : ""}`}
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border border-[#264839]/40 text-[#264839] bg-[#F0EFEA]" data-testid="search-sort-label" title="Verified + Category Leaders shown first; Free listings appear at the bottom.">
            Sort · Recommended
          </div>
        </div>
        {results.items.length === 0 && q ? (
          <div className="bg-[#F0EFEA] p-12 text-center text-[#7D8C85] rounded-sm" data-testid="empty-with-rfq">
            <div className="font-serif text-xl text-[#11211A]" data-testid="search-empty-title">No suppliers found for “{q}”</div>
            <div className="text-sm mt-2">Try another word, or send one request and let matching suppliers come to you.</div>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-4">
              <button type="button" onClick={() => setRfqOpen(true)} disabled={!rfqCategory} className="btn-primary disabled:opacity-50" data-testid="search-empty-rfq">Request quotes</button>
              <button type="button" onClick={clearSearch} className="text-sm text-[#B35C4A] hover:underline" data-testid="search-empty-clear">Clear search</button>
            </div>
          </div>
        ) : results.items.length === 0 ? (
          <div className="bg-[#F0EFEA] p-8 text-center rounded-sm" data-testid="empty-with-rfq">
            <div className="text-[#7D8C85] mb-4">No suppliers found for this filter.</div>
            <div className="border-2 border-dashed border-[#B35C4A]/50 bg-[#B35C4A]/5 px-5 py-4 flex items-center gap-3 text-left">
              <FileText className="w-5 h-5 text-[#B35C4A] shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium text-[#11211A]">Can't find it? Post an RFQ and we'll source it for you.</div>
                <div className="text-[11px] text-[#7D8C85] mt-0.5">Our sourcing team hunts down suppliers who match your brief within 48 hours.</div>
              </div>
              <a href="mailto:sourcing@accommodationsuppliers.com?subject=Custom%20sourcing%20request" className="btn-primary shrink-0" data-testid="post-rfq-btn">Post an RFQ</a>
            </div>
          </div>
        ) : (
          <>
            <ListingGrid items={results.items} freeTotal={results.free_total} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" />
            <div className="mt-8 flex flex-col items-center gap-3" data-testid="search-pagination">
              <div className="text-xs uppercase tracking-[0.15em] text-[#7D8C85]" data-testid="search-showing-count">
                Showing {results.items.length} of {results.total} supplier{results.total === 1 ? "" : "s"}
              </div>
              {results.items.length < results.total && (
                <button type="button" onClick={loadMore} disabled={loadingMore} className="btn-secondary disabled:opacity-50" data-testid="search-load-more">
                  {loadingMore ? "Loading…" : `Load ${Math.min(PAGE_SIZE, results.total - results.items.length)} more supplier${results.total - results.items.length === 1 ? "" : "s"}`}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {rfqOpen && rfqCategory && (
        <CategoryRFQModal open={rfqOpen} onClose={() => setRfqOpen(false)} category={rfqCategory} topListings={results.items.slice(0, 4)} />
      )}
    </div>
  );
}
